/**
 * What a blank pipeline cell means.
 *
 * A missing requested amount has three different causes and the grid rendered
 * all three identically — as "$0" before, as "—" now. On the live desk the
 * three deals with no amount were: seven rival amounts the parser refused to
 * choose between, two rival amounts, and a package that never stated one. Two
 * of those are a decision somebody owes; the third is a document nobody has.
 *
 * This marks the cell with which it is, and opens the Data Intelligence Review
 * on that deal and field, where the decision is actually made: pick one of the
 * rivals, type a value, point the engine at the document, or record that it is
 * not available — each an answer, so a "not stated" cell opens it too. It never
 * shows the rival values itself; choosing between them belongs there, with the
 * evidence attached.
 */
import { createContext, useContext, useMemo } from 'react'
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import type { PipelineReviewGaps } from '@/services/loans'
import { dateInputValue } from '@/utils/dates'

export type ReviewGap = {
  status: string
  candidates: number
  ingestionId?: string | null
  receivedAt?: string | null
}

/** Statuses that question a value the cell DOES show. The rest explain a
 *  blank, so on a filled cell they have nothing to say. Rendering markers only
 *  on blank cells hid every one of these (stress test OVR-09). */
const QUESTIONS_A_FILLED_CELL = new Set(['conflict', 'revision_pending', 'data_conflict', 'name_unconfirmed'])

/** The statuses the Data Intelligence Review answers. A pending revision is
 *  settled in the email's thread panel, and a sweep finding is not a question
 *  the review lists, so those two keep opening the record they belong to. */
const ANSWERED_IN_REVIEW = new Set(['conflict', 'derivable', 'flagged', 'missing', 'name_unconfirmed'])

type ReviewGapContextValue = {
  gaps: PipelineReviewGaps['gaps']
  /** Opens the Data Intelligence Review on a deal and one of its fields. */
  onOpen?: (loanId: string, field: string) => void
}

const ReviewGapContext = createContext<ReviewGapContextValue>({ gaps: {} })

export function ReviewGapProvider({
  gaps,
  onOpen,
  children,
}: {
  gaps: PipelineReviewGaps['gaps'] | undefined
  onOpen?: (loanId: string, field: string) => void
  children: ReactNode
}) {
  const value = useMemo(() => ({ gaps: gaps ?? {}, onOpen }), [gaps, onOpen])
  return <ReviewGapContext.Provider value={value}>{children}</ReviewGapContext.Provider>
}

export function useReviewGap(loanId: string, field: string): ReviewGap | null {
  const { gaps } = useContext(ReviewGapContext)
  return gaps[loanId]?.[field] ?? null
}

/** How each open status reads to a person, and whether it is answerable. */
function describe(gap: ReviewGap, canReview: boolean): { label: string; title: string; answerable: boolean } {
  const n = gap.candidates
  if (gap.status === 'revision_pending') {
    // The day the desk got it, in the reader's zone: slicing the UTC stamp
    // dated a 9:30pm New York email the next day (N2).
    const day = dateInputValue(gap.receivedAt)
    const when = day ? ` (${day})` : ''
    return {
      label: 'revision pending',
      title:
        `A later email${when} states a different figure that waits for a person. The deal ` +
        'keeps this value until someone applies or keeps the new one. Select to review the email.',
      answerable: true,
    }
  }
  if (gap.status === 'data_conflict') {
    return {
      label: 'check figure',
      title:
        'The reconciliation sweep found this figure disagreeing with the arithmetic of the ' +
        "deal's other inputs. Select to open the intake record it was read from.",
      answerable: true,
    }
  }
  if (gap.status === 'name_unconfirmed') {
    // J01 "88-unit multifamily - Boise": a description from the email
    // subject, promoted as the deal's name before intake held such names.
    return {
      label: 'name unconfirmed',
      title:
        'This deal is named from the email subject line; no document names the property. ' +
        'Select to confirm or correct the name in the Data Intelligence Review.',
      answerable: true,
    }
  }
  if (gap.status === 'conflict') {
    return {
      label: n > 0 ? `${n} rival${n === 1 ? '' : 's'}` : 'in conflict',
      title:
        n > 0
          ? `The package states this ${n} different ways and the parser did not choose. ` +
            'Select to settle it in the Data Intelligence Review.'
          : 'The package contradicts itself here. Select to settle it in the Data Intelligence Review.',
      answerable: true,
    }
  }
  if (gap.status === 'derivable') {
    return {
      label: 'derivable',
      title:
        'Calculable from values the package does state. Select to confirm the calculation in the Data Intelligence Review.',
      answerable: true,
    }
  }
  if (gap.status === 'flagged') {
    return {
      label: 'flagged',
      title: 'Extracted but flagged for a person to check. Select to review it in the Data Intelligence Review.',
      answerable: true,
    }
  }
  return {
    label: 'not stated',
    title: canReview
      ? 'No document in the package states this. Select to enter it, point the engine at it in a ' +
        'document, or record that it is not available.'
      : 'No document in the package states this. It needs a source or a person — there is ' +
        'nothing here to confirm.',
    answerable: canReview,
  }
}

/**
 * The marker itself. Rendered beside the em dash, never instead of it: the
 * cell still reads as empty, because it is.
 */
export function ReviewGapMarker({
  loanId,
  field,
  populated = false,
  ingestionId,
}: {
  loanId: string
  field: string
  /** The cell shows a value: only statuses that question a value render. */
  populated?: boolean
  /** The deal's source intake record — where its review lives when the page
   *  cannot open the review in place. */
  ingestionId?: string | null
}) {
  const gap = useReviewGap(loanId, field)
  const { onOpen } = useContext(ReviewGapContext)
  const navigate = useNavigate()
  if (!gap) return null
  if (populated && !QUESTIONS_A_FILLED_CELL.has(gap.status)) return null
  const canReview = Boolean(onOpen) && ANSWERED_IN_REVIEW.has(gap.status)
  const { label, title, answerable } = describe(gap, canReview)
  const className =
    'ml-1 shrink-0 rounded border px-1 py-px text-[8px] font-medium leading-none ' +
    (answerable
      ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
      : 'border-stone-700 text-stone-500')
  const target = gap.ingestionId ?? ingestionId ?? null
  // Grid cells render inside a row button, and a button inside a button is
  // invalid markup: an element with the button role, not a <button>.
  const asControl = (open: (event: MouseEvent | KeyboardEvent) => void, role: 'button' | 'link') => (
    <span
      role={role}
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          open(event)
        }
      }}
      title={title}
      aria-label={`${field.replace(/_/g, ' ')}: ${title}`}
      className={`${className} cursor-pointer hover:border-amber-300/60`}
    >
      {label}
    </span>
  )
  if (canReview) {
    return asControl((event) => {
      // The row beneath is a button that selects the deal.
      event.stopPropagation()
      onOpen!(loanId, field)
    }, 'button')
  }
  if (answerable && target) {
    // A revision is settled in the email's thread panel, and a sweep finding
    // is not a review item; everything else, when this page cannot open the
    // review itself, is settled in the record's review, which the deep link
    // opens directly.
    const review = gap.status === 'revision_pending' || gap.status === 'data_conflict' ? '' : '&review=1'
    return asControl((event) => {
      event.stopPropagation()
      navigate(`/intake?focus=${encodeURIComponent(target)}${review}`)
    }, 'link')
  }
  return (
    <span title={title} aria-label={`${field.replace(/_/g, ' ')}: ${title}`} className={className}>
      {label}
    </span>
  )
}
